import { defineMessages } from 'react-intl';

export const scope = 'app.components.ApprovalLevelChain';

/**
 * Copy for the shared approval level chain.
 *
 * The state words follow the Design Requirements' own vocabulary where it has
 * one: §5 says "Not triggered" for a conditional level whose predicate did not
 * match, and §8 says "Cancelled" for an approver the outcome overtook. Only
 * `active` has no ratified string, and "Awaiting decision" says more than
 * "Active" about who is being waited on.
 *
 * "Level" is the domain noun and is deliberate: an approval level is a round of
 * sign-off inside a rule, not a workflow stage, and the shipped rule editor
 * already labels them "Level 1 · Finance review". The level labels themselves are
 * imported from that catalog rather than restated here.
 */
export default defineMessages({
  // --- a level's own state ---
  levelApproved: {
    id: `${scope}.levelApproved`,
    defaultMessage: 'Approved',
    description: 'State of a level of sign-off that has been satisfied',
  },
  levelRejected: {
    id: `${scope}.levelRejected`,
    defaultMessage: 'Rejected',
    description: 'State of a level of sign-off an approver refused',
  },
  levelActive: {
    id: `${scope}.levelActive`,
    defaultMessage: 'Awaiting decision',
    description:
      'State of a level of sign-off that is open and being asked of its approvers right now',
  },
  levelNotStarted: {
    id: `${scope}.levelNotStarted`,
    defaultMessage: 'Not started',
    description:
      'State of a level not reached yet, because an earlier level in the same rule is still open',
  },
  levelNotTriggered: {
    id: `${scope}.levelNotTriggered`,
    defaultMessage: 'Not triggered',
    description:
      'State of a conditional level whose condition was checked and did not match, so nobody was asked',
  },
  levelCancelled: {
    id: `${scope}.levelCancelled`,
    defaultMessage: 'Cancelled',
    description:
      'State of a level that did apply but was closed by an outcome elsewhere in the request, typically a rejection at another level',
  },

  levelStateLabel: {
    id: `${scope}.levelStateLabel`,
    defaultMessage: 'Level {state}',
    description:
      "Accessible name of the pill carrying a level's own state, and the screen-reader-only text beside a level's name where no pill is drawn. The visible word is the state on its own, which Design Requirements §5 ratifies — but an approver row on the same level can carry the same word, and the two are indistinguishable read aloud. {state} is the state's own label",
  },

  // --- one approver's own state ---
  approverApproved: {
    id: `${scope}.approverApproved`,
    defaultMessage: 'Approved',
    description: 'State of one approver who has signed off',
  },
  approverRejected: {
    id: `${scope}.approverRejected`,
    defaultMessage: 'Rejected',
    description: 'State of one approver who has refused the offer',
  },
  approverPending: {
    id: `${scope}.approverPending`,
    defaultMessage: 'Pending',
    description:
      'State of one approver who has not responded yet, on a level that is still running',
  },
  approverCancelled: {
    id: `${scope}.approverCancelled`,
    defaultMessage: 'Cancelled',
    description:
      'State of one approver who never answered and no longer will, because the level closed without them — saying "Pending" here would claim a decision is still expected on a request that is over',
  },
  approverCannotApprove: {
    id: `${scope}.approverCannotApprove`,
    defaultMessage: 'Cannot approve',
    description:
      'State of one approver the level still waits on who cannot act: they left the account or lost the permission to approve. The line under it says which',
  },
  approverRemoved: {
    id: `${scope}.approverRemoved`,
    defaultMessage: 'No longer a user in this account',
    description:
      'Why an approver shown as "Cannot approve" cannot act: their user was removed from the account',
  },
  approverNoPermission: {
    id: `${scope}.approverNoPermission`,
    defaultMessage:
      '{subject, select, Funnel {No longer has permission to approve openings} other {No longer has permission to approve offers}}',
    description:
      'Why an approver shown as "Cannot approve" cannot act: they no longer hold the permission to approve this kind of request. `subject` is the request type, Funnel for an opening',
  },

  // --- notes on a level ---
  levelProgress: {
    id: `${scope}.levelProgress`,
    defaultMessage: '{approved} of {total} approved',
    description:
      'Progress of a level that needs one sign-off per primary approver, so a partly-complete level says how far along it is rather than only listing states. {approved} is how many people have approved. {total} is how many sign-offs the level needs, which is fewer than the approvers listed once backups have joined',
  },
  levelNotTriggeredNote: {
    id: `${scope}.levelNotTriggeredNote`,
    defaultMessage: 'This level did not apply to this offer',
    description:
      'Explains a level nobody was asked, in place of its approver list. Distinct from a level that was approved, and from one an outcome elsewhere cancelled',
  },
  levelCancelledNote: {
    id: `${scope}.levelCancelledNote`,
    defaultMessage: 'Closed before these approvers were asked',
    description:
      'Explains a level that did apply but was closed by an outcome elsewhere in the request — its approvers were never asked, but not because the level was irrelevant',
  },
  levelEscalated: {
    id: `${scope}.levelEscalated`,
    defaultMessage: 'Escalated to backup approvers',
    description:
      'Note on a level whose escalation timer expired, so its backup approvers can now act as well',
  },
  levelEscalatesAt: {
    id: `${scope}.levelEscalatesAt`,
    defaultMessage: 'Escalates to backup approvers on {date}',
    description:
      "Note on a level that is still waiting, saying when its backup approvers join in if nobody has answered by then. {date} is the deadline on the reader's own clock",
  },
  levelNeedsAttention: {
    id: `${scope}.levelNeedsAttention`,
    defaultMessage:
      'No approver could be found for this level, so it cannot clear. Ask an admin to check the rule.',
    description:
      'Warning on a level that is open but can never be satisfied, because approver routing resolved to nobody. Says what to do next',
  },
  levelHeld: {
    id: `${scope}.levelHeld`,
    defaultMessage:
      'Held for review — the offer is missing the information this level checks.',
    description:
      'Note on a level open because its condition could not be checked rather than because it matched, so an approver knows they are being asked as a precaution',
  },
  noApprovers: {
    id: `${scope}.noApprovers`,
    defaultMessage: 'No approvers on this level',
    description:
      'Stands in for the approver list of a level nobody was resolved onto',
  },
  backupApprover: {
    id: `${scope}.backupApprover`,
    defaultMessage: 'Backup',
    description:
      'Marks an approver who only counts once the level has escalated to them',
  },
  // PRD "Approvers by Opening Owner and attributes" §5.6: "Sam Lee · Opening
  // owner", and on fallback "Jane Doe · Backup (no HRBP Email on this
  // opening)". {source} is the server's `source_label`, recorded at submission.
  approverSourceBeside: {
    id: `${scope}.approverSourceBeside`,
    defaultMessage: '· {source}',
    description:
      'Follows an approver\'s name to say what picked them, such as "Opening owner". {source} is that label, or one of the backup notes below',
  },
  approverFallbackNoValue: {
    id: `${scope}.approverFallbackNoValue`,
    defaultMessage: 'Backup (no {source} on this opening)',
    description:
      'Why a backup approver was asked from the start: the opening has nobody in the role the rule names, such as no opening owner. {source} names that role, such as "Opening owner"',
  },
  approverFallbackOtherAccount: {
    id: `${scope}.approverFallbackOtherAccount`,
    defaultMessage: 'Backup ({source} is in another account)',
    description:
      'Why a backup approver was asked from the start: the person in the role the rule names, such as the opening owner, is a user of a different company account. {source} names that role',
  },
  approverFallbackNoPermission: {
    id: `${scope}.approverFallbackNoPermission`,
    defaultMessage:
      "{subject, select, Funnel {Backup ({source} can't approve openings)} other {Backup ({source} can't approve offers)}}",
    description:
      'Why a backup approver was asked from the start: the person in the role the rule names, such as the opening owner, lacks the permission to approve this kind of request. {source} names that role. `subject` is the request type, Funnel for an opening',
  },
  unnamedApprover: {
    id: `${scope}.unnamedApprover`,
    defaultMessage: 'Approver no longer in this account',
    description:
      'Stands in for the name of an approver whose user record has been removed, so a level still lists everyone who was asked rather than showing a blank row',
  },
});
